import { APP_LOCALE } from "../locale";

/**
 * Slice-local typed message keys for the collected-data library.
 *
 * The central `messages.ts` is owned by another slice, so this module follows
 * the same typed-key pattern locally (as `whatsapp/messages.ts` does) and the
 * integration step can lift these keys into the shared catalogue verbatim. Only
 * `nav.sources` is read from the shared catalogue, because the sidebar label is
 * already there.
 *
 * The wording carries one rule throughout: a gap is stated, never implied. There
 * is no phrasing here that lets "we have not read this yet" read as "there is
 * nothing here".
 */

export const sourcesMessages = {
  title: "Toplanan veri",
  body: "Obsina'nın bağladığınız kaynaklardan envantere aldığı her şey. Sakladığı metni okumak için bir satırı açın.",

  filters: {
    channel: {
      label: "Kanal",
      all: "Tüm kanallar",
      mail: "Posta",
      attachment: "Ekler",
      document: "Belgeler",
      drive: "Dosyalar",
      teams: "Teams",
      calendar: "Takvim",
      whatsapp: "WhatsApp",
    },
    // One mail with six images put six "Adsız PNG image" rows wherever the
    // keyset happened to put them. Folded under the mail they arrived in, the
    // list reads as what it is. The flat position is kept as a choice because
    // the server's own order is the one a reader compares against a cursor.
    grouping: {
      label: "Ekler",
      grouped: "Geldiği öğenin altında",
      flat: "Ayrı satır",
    },
    // The five reading verdicts the server filters on. They describe what a
    // reading found, which is a different question from whether there is
    // anything to open now — see `state` below.
    text: {
      label: "Metin",
      all: "Her durum",
      success: "Tamamı okundu",
      partial: "Kısmen okundu",
      unsupported: "Biçimi okuyan yok",
      failed: "Okunamadı",
      pending: "Henüz okunmadı",
    },
  },

  /**
   * What the `Text` column says on a row. Four of the words are the reading
   * verdicts above, and `withdrawn` is the one the list did not have: an item
   * read in full whose document is no longer published. Without it the column
   * printed "Tamamı okundu" over a row whose own dialog said the text was not
   * published — one row, two opposite claims.
   */
  state: {
    success: "Tamamı okundu",
    partial: "Kısmen okundu",
    unsupported: "Biçimi okuyan yok",
    failed: "Okunamadı",
    pending: "Henüz okunmadı",
    withdrawn: "Metin geri çekildi",
  },

  table: {
    regionLabel: "Toplanan öğeler",
    item: "Öğe",
    channel: "Kanal",
    text: "Metin",
    size: "Boyut",
    date: "Tarih",
    open: (title: string) => `${title} — aç`,
    // What a row is called when the provider recorded no name for it. Three
    // wordings rather than one, because "No title recorded" beside an image is
    // less than the surface knows: it can say the format instead, which is the
    // one fact that distinguishes these rows from each other. None of them
    // repeats the channel — the column beside it already carries that.
    untitled: "Ad kaydedilmemiş",
    untitledMessage: "Konu kaydedilmemiş",
    untitledFormat: (format: string) => `Adsız ${format}`,
    // One row can be an attachment of another. Said on the row it applies to,
    // not repeated as a column.
    attachedTo: "Ek",
    // What the fold says on the row that holds it. Deliberately "in this list":
    // the inventory is paged, so a later page can add attachments of this same
    // item, and a bare "6 ek" would claim a total this page cannot know.
    attachments: (count: number) => `${count} ek — bu listede`,
    attachmentsShow: (title: string, count: number) =>
      `${title} içindeki ${count} eki göster`,
    attachmentsHide: (title: string) => `${title} içindeki ekleri gizle`,
    // The size column when what was measured is text, not bytes. Same wording as
    // the dialog's own count, so one item never reads two ways.
    characters: (count: number) =>
      `${count.toLocaleString(APP_LOCALE)} karakter`,
  },

  // The shared, once-per-page statements. Anything true of every row belongs
  // here rather than on each row.
  summary: {
    // Türkçede sayıdan sonra çoğul eki gelmez, bu yüzden tek kalıp yeter:
    // "1 öğe gösteriliyor" ve "12 öğe gösteriliyor" aynı şablondan çıkar.
    shown: (count: number) => `${count} öğe gösteriliyor`,
    more: "Daha fazla",
    moreFailed: "Daha fazla öğe yüklenemedi. Yeniden deneyin.",
    // Said after a page arrived, not instead of one. The list is bound to a
    // reading snapshot, and sync moves that snapshot while a page is open; when
    // it moved, this surface re-read the list from the start rather than
    // stopping. What changed is the cut-off above, so that is what this names.
    moreRestarted:
      "Okuma sürerken yeni veri geldi; liste güncel okumaya göre yenilendi.",
    complete: "Bağladığınız kaynaklardaki her şey bu.",
    asOf: (asOf: string) => `${asOf} tarihine kadar okundu.`,
  },

  coverage: {
    partial: (reconciled: number, required: number) =>
      `Bağlı ${required} kaynaktan ${reconciled} tanesi tamamen yetişti; kalanların arkasındaki öğeler bu listede eksik olabilir.`,
    unknownTitle: "Bu liste şimdilik gösterilemiyor",
    unknownBody:
      "Obsina bağlı kaynaklarınızın ne kadarını okuduğunu saptayamıyor; bu yüzden eksiksiz gibi okunabilecek bir liste göstermez. Kaynaklar yetiştikçe bu durum çözülür.",
  },

  empty: {
    title: "Henüz hiçbir şey toplanmadı",
    body: "Bağlı bir kaynak okunduğunda içindeki her öğe burada listelenir.",
  },

  errors: {
    unauthorizedTitle: "Oturumunuzun süresi doldu",
    unauthorizedBody: "Toplanan verinizi okumak için yeniden oturum açın.",
    unauthorizedAction: "Yeniden oturum aç",
    unavailableTitle: "Okuma yüzeyi kullanılamıyor",
    unavailableBody:
      "Bilgi okuma yüzeyi şu anda istek karşılamıyor. Hiçbir şey kaybolmadı; birazdan yeniden deneyin.",
    genericTitle: "Toplanan veri yüklenemedi",
    genericBody: "İstek tamamlanmadı. Yeniden deneyin.",
    retry: "Yeniden dene",
  },

  dialog: {
    close: "Kapat",
    // The provenance line. One sentence, stated once, above the text.
    meta: {
      visibilityPrivate: "Yalnızca size görünür",
      visibilityShared: "Çalışma alanınızdaki herkes görebilir",
      derived: "Sağlayıcının kendi metni değil, bir görselden OCR ile okundu",
      canonical: "Sağlayıcının kendi metni, alındığı gibi korunuyor",
      // Çoğul eki kalkar; shown ≠ total dallanması KALIR — o sayı değil
      // durum ayrımıdır (tamamı mı, bir kısmı mı gösteriliyor).
      blocks: (shown: number, total: number) =>
        shown === total
          ? `${total} blok`
          : `${total} bloktan ${shown} tanesi gösteriliyor`,
      characters: (count: number) =>
        `${count.toLocaleString(APP_LOCALE)} karakter`,
    },
    moreBlocks: "Metnin devamını göster",
    /**
     * Why a partial reading stopped short, in the reader's words.
     *
     * One sentence per bucket the pipeline actually records
     * (`media_partial_reason_heads`), never a cause inferred from the channel
     * the item arrived on. Measured on the live tenant on 2026-08-07:
     * `no_meaningful_text` 253, `low_confidence` 123, `unattributed` 13,
     * `rejected` 0 — and 69 partial items with no reading behind them at all,
     * which is {@link partialCauseAbsent}.
     *
     * None of them names the confidence value or the threshold: those stay
     * behind the derived-layer boundary, and a number a reader cannot act on
     * would be precision standing in for an answer.
     */
    partialCause: {
      low_confidence:
        "Bir okuma bu öğeden metin çıkardı, ama Obsina'nın güven eşiğinin altında kaldı; eşiğin altındaki bir okumayı öğenin metni diye sunmaz.",
      no_meaningful_text:
        "Bir okuma bu öğe üzerinde çalıştı ve anlamlı bir metin bulamadı; çıkan şey öğenin içeriği sayılacak kadarını taşımıyordu.",
      rejected:
        "Okuma tamamlandı, ama belge katmanı bu okumadan bir belge yayımlamayı reddetti.",
      // The pipeline has a reading and it accounts for nothing. Deliberately
      // not the same sentence as an absent reading: one says the record does
      // not explain the verdict, the other says there is no record to explain
      // it, and collapsing them would state a fact neither of them holds.
      unattributed:
        "Bu öğenin kayıtlı okuması, neden kısmi kaldığını açıklamıyor.",
    },
    /** No reading stands behind the item, so nothing classified the shortfall. */
    partialCauseAbsent:
      "Bu öğenin arkasında kayıtlı bir okuma yok; kısmiliğin nedeni saptanamıyor.",
    loading: "Metin yükleniyor…",
    loadFailed: "Metin yüklenemedi. Yeniden deneyin.",
    retry: "Yeniden dene",
    openSource: "Kaynağında aç",

    // Why there is no text to show. Each states what happened and whether
    // waiting helps — never "no data".
    absent: {
      pending: {
        title: "Henüz okunmadı",
        body: "Obsina bu öğeyi tutuyor, metnini henüz okumadı. Boru hattı ulaştığında burada görünecek.",
      },
      /**
       * `media_partial` is classified `terminal` in `@obsina/domain`: the same
       * bytes read again return the same verdict. So this says the one thing the
       * old wording left the reader to guess — whether waiting helps — and says
       * it as the reason it is, not as a promise withheld.
       *
       * What it still cannot say is why *this* item stopped short. The pipeline
       * records that (`app_private.media_partial_reason_heads` derives one of
       * low_confidence / no_meaningful_text / rejected / unattributed per item),
       * but no API this surface reads projects it, so the gap is stated rather
       * than filled with a plausible cause. See {@link partialCauseUnknown}.
       */
      partial: {
        title: "Yalnızca kısmen okundu",
        body: "Bu öğeden okunabilen parça bir belge oluşturmaya yetmedi; Obsina onu saklar ama öğenin metni diye göstermez. Aynı baytların yeniden okunması aynı sonucu verir, bu yüzden bu öğe kendiliğinden yeniden okunmaz.",
      },
      unsupported: {
        title: "Bu biçimi okuyan yok",
        body: "Obsina bu öğenin baytlarını tutuyor ama biçimini okuyan bir okuyucusu yok. Beklemek bunu değiştirmez.",
      },
      failed: {
        title: "Okunamadı",
        body: "Bu öğeyi okuma denemelerinin tümü tükendi. Gösterilecek metin yok; beklemek bunu değiştirmez.",
      },
      // Two ways a fully read item ends up with nothing to open. They replace
      // the single "Metin yayımlanmadı" that used to cover both: that wording
      // described the internal state accurately and told the reader nothing
      // about their own data. Neither promises the text will come back, because
      // neither will.
      withdrawn: {
        removed: {
          title: "Kaynağında silindi",
          body: "Bu öğe tamamen okundu ve geldiği kaynakta artık yok; bu yüzden Obsina metnini geri çekti. Burada bulunduğunun kaydı kalır.",
        },
        stale: {
          title: "Kaynak değişince metin geri çekildi",
          body: "Bu öğe tamamen okundu. Ait olduğu ileti sonradan değişti; Obsina, kaynağın şu anda tuttuğuyla artık bağdaştıramadığı metni göstermek yerine okumayı geri çekti.",
        },
      },
      success: {
        title: "Metin yayımlanmadı",
        body: "Bu öğe okundu ama onun için yayımlanmış güncel bir belge yok. Obsina, güncel olduğuna güvence veremediği metni göstermez.",
      },
    },

    /**
     * Replaces the body above when the pipeline recorded which of the things
     * that status can mean actually happened. It replaces rather than joins it:
     * two sentences saying the same thing in different words is the repetition
     * this surface avoids, and the specific one is always the better of the
     * two. The heading stays, so the reader still sees the verdict they saw in
     * the list.
     */
    reason: {
      no_reader_for_format:
        "Obsina bu öğenin baytlarını tutuyor; sahip olduğu hiçbir okuyucu bu biçimi üstlenmiyor. Başka bir deneme yapılmayacak.",
      reading_failed:
        "Bir okuyucu bu öğenin baytları üzerinde çalıştı ve hiçbir metin çıkaramadı. Denemeleri tükendi.",
    },
    /**
     * The document itself was refused, and by what. Three sentences rather than
     * one, because the old single wording — "cannot be shown **yet**" — made a
     * promise nobody had made. It was printed over a shortfall the envelope
     * itself classified as `terminal`: OCR readings refused below the confidence
     * floor, formats no reader claims, attempts already spent. None of those
     * resolve, and "yet" told every reader to wait for them.
     */
    withheld: {
      // The item's own reading is a fragment. Nothing about the rest of the
      // tenant changes that, so this says nothing about waiting.
      incomplete: {
        title: "Bu öğenin yalnızca bir kısmı okunabildi",
        body: "Obsina kısmen okunmuş bir öğeyi tam belge gibi sunmaz; parçayı bu öğenin metni olarak göstermek yerine saklı tutar. Aynı baytların yeniden okunması aynı sonucu verir, bu yüzden bu öğe kendiliğinden yeniden okunmaz.",
      },
      // Coverage is not established and something behind it can still move.
      transient: {
        title: "Kaynaklar yetişirken metin kullanılamıyor",
        body: "Obsina bağlı kaynaklarınızın tuttuğunun bir kısmını okumayı sürdürüyor ve arkasındaki kaynağın ne kadarını okuduğunu söyleyebilene dek bir belgeyi açmaz. Bu durum kendiliğinden çözülür.",
      },
      // Coverage is not established and nothing behind it will move again.
      terminal: {
        title: "Metin kullanılamıyor",
        body: "Bağlı kaynaklarınızın tuttuğunun bir kısmı okunamadı ve okunmayacak — biçimlerini okuyan yok ya da denemeleri tükendi — bu yüzden Obsina bu belgenin arkasındaki kaynağın ne kadarını okuduğunu saptayamıyor. Öğe envanterinizde kalır.",
      },
      // Durability was not stated. Neither promise is made.
      unstated: {
        title: "Metin kullanılamıyor",
        body: "Arkasındaki kaynağın ne kadarını okuduğunu saptayamadığı sürece Obsina belgeyi açmaz. Öğe envanterinizde; yalnız metni gösterilmiyor.",
      },
    },
  },
} as const;

export type SourcesMessages = typeof sourcesMessages;
